import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Info } from 'lucide-react';
import { attendanceService, employeeService, authService, attendanceStructureService } from '../services/api';
import { syncAttendancePoliciesCache } from '../lib/syncAttendancePoliciesCache';
import { useAttendanceCodeConfig } from '../context/attendanceCodeConfigContext';
import EmployeeAttendanceCalendarPopup from '../components/Admin/attendance/components/EmployeeAttendanceCalendarPopup';
import LeaveBalancePopup from '../components/Admin/attendance/components/popups/LeaveBalancePopup';
import CompOffLedgerPopup from '../components/Admin/attendance/components/popups/CompOffLedgerPopup';
import ViewEditChecksPopup from '../components/Admin/attendance/components/popups/ViewEditChecksPopup';
import QuickRequestFormModal from '../components/Admin/attendance/components/QuickRequestFormModal';
import { usePermissions } from '../rbac/PermissionsContext';
import { getLocalDayKey, ATTENDANCE_PUNCH_UPDATED_EVENT, hydrateEmployeeWorklogFromAttendance } from '../lib/attendancePunchStorage';
import { socket } from '../services/websocket';
import { formatPunchDateTimeDisplay, parsePunchInstantMs, resolveDayDetailHours } from '../lib/attendanceDayChecks';
import {
  emptyAttendanceDeskDraft,
  submitAttendanceActionRequest,
  submitChangeShiftRequest,
  submitChangeWoRequest,
  submitShortLeaveRequest,
  submitRestrictedHolidayRequest,
  submitCompOffRequest,
  submitOvertimeRequest,
} from '../lib/attendanceDeskRequests';
import {
  consumeOpenRestrictedHolidayForm,
  employeeHasRestrictedHolidayOption,
} from '../lib/restrictedHolidayAccess';
import {
  cacheWeeklyOffStructureData,
  CHANGE_WO_ACTION_LABEL,
} from '../lib/weeklyOffChange';
import {
  getDefaultStatus,
  normalizeEmployeeWeeklyOffMapping,
  normalizeWeeklyOffPolicies,
} from '../lib/attendanceDayDefaults';
import { getAuthJson } from '../utils/authStorage';

const statusStyles = {
  'PR': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'WO': 'bg-gray-100 text-gray-800 border-gray-200',
  'PH': 'bg-purple-100 text-purple-800 border-purple-200',
  'HO': 'bg-purple-100 text-purple-800 border-purple-200',
  'CL': 'bg-amber-100 text-amber-800 border-amber-200',
  'EL': 'rh-accent-soft border-redhalo-teal/80',
  'LOP': 'bg-red-100 text-red-800 border-red-200',
  'KL': 'bg-amber-100 text-amber-800 border-amber-200',
  'ML': 'bg-sky-100 text-sky-800 border-sky-200',
  'BL': 'bg-slate-100 text-slate-800 border-slate-200',
  'CO': 'bg-orange-100 text-orange-800 border-orange-200',
  'CW': 'bg-teal-100 text-teal-800 border-teal-200',
};

// Default config codes (fallback when API not available)
const DEFAULT_LEGEND_CONFIGS = [
  { meaning: 'Present', originalCode: 'PR', customCode: 'PR' },
  { meaning: 'Absent', originalCode: 'LOP', customCode: 'LP' },
  { meaning: 'Weekly Off', originalCode: 'WO', customCode: 'WO' },
  { meaning: 'Public Holiday', originalCode: 'PH', customCode: 'PH' },
];

export default function EmployeeAttendance() {
  const { mapping, reverseMapping } = useAttendanceCodeConfig();
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canViewLeaveBalance =
    isAdmin ||
    hasScreenAccess('home', 'ess_leave_balance', 'view') ||
    hasScreenAccess('ess_attendance', 'leave_balance', 'view');
  const canViewCompOffLedger =
    isAdmin ||
    canViewLeaveBalance ||
    hasScreenAccess('ess_attendance', 'comp_off_ledger', 'view');
  const canViewChecks =
    isAdmin ||
    hasScreenAccess('ess_attendance', 'view_checks', 'view') ||
    hasScreenAccess('ess_attendance', 'self_service', 'view');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceDetailData, setAttendanceDetailData] = useState({});
  const [attendanceLocks, setAttendanceLocks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLocked, setIsLocked] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [legendConfigs, setLegendConfigs] = useState([]);
  const [legendLeaveTypes, setLegendLeaveTypes] = useState([]);
  const [weeklyOffPolicies, setWeeklyOffPolicies] = useState([]);
  const [employeeWeeklyOffMappings, setEmployeeWeeklyOffMappings] = useState({});
  const [holidaysData, setHolidaysData] = useState([]);
  const [holidayPolicies, setHolidayPolicies] = useState([]);
  const [employeeHolidayMappings, setEmployeeHolidayMappings] = useState({});
  const [woDeskTick, setWoDeskTick] = useState(0);
  const [showLeaveBalancePopup, setShowLeaveBalancePopup] = useState(false);
  const [showCompOffLedgerPopup, setShowCompOffLedgerPopup] = useState(false);
  const [showViewChecksPopup, setShowViewChecksPopup] = useState(false);
  const [viewChecksDate, setViewChecksDate] = useState(null);

  // Fetch admin-configured default codes (Configuration) and leave types (Leave Management) for legend
  useEffect(() => {
    const loadLegendData = async () => {
      try {
        const [configs, ltResp] = await Promise.all([
          attendanceService.getConfigurations().catch(() => []),
          attendanceStructureService.getLeaveTypes().catch(() => ({ success: false, data: [] })),
        ]);
        setLegendConfigs(Array.isArray(configs) && configs.length > 0 ? configs : DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes(Array.isArray(ltResp?.data) ? ltResp.data : []);
      } catch (e) {
        console.warn('Failed to load legend data:', e);
        setLegendConfigs(DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes([]);
      }
    };
    loadLegendData();
  }, []);

  // Fetch employee profile to get joining date (use only employeeId – works for any logged-in employee)
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        const currentUserResponse = await authService.getCurrentUser();
        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data;
          const employeeId = userData.employeeId;
          if (employeeId) {
            const employeeResponse = await employeeService.getEmployee(employeeId);
            if (employeeResponse && employeeResponse.data) {
              setEmployee(employeeResponse.data);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch employee data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeData();
  }, []);

  // Fetch attendance locks
  useEffect(() => {
    const loadLocks = async () => {
      try {
        const response = await attendanceStructureService.getAttendanceLocks();
        if (response.success && Array.isArray(response.data)) {
          setAttendanceLocks(response.data || []);
          const currentLock = response.data.find(
            (lock) => lock.month === selectedMonth + 1 && lock.year === selectedYear
          );
          setIsLocked(!!currentLock);
        } else {
          setAttendanceLocks([]);
          setIsLocked(false);
        }
      } catch (err) {
        console.error('Failed to load attendance locks:', err);
        setAttendanceLocks([]);
        setIsLocked(false);
      }
    };
    loadLocks();
  }, [selectedMonth, selectedYear]);

  // Fetch weekly off, holidays and policy mappings (for default PR/WO/PH when locked and backend is empty)
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const [woResp, woMapResp, holidaysResp, hpResp, hpMapResp] = await Promise.all([
          attendanceStructureService.getWeeklyOffPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeWeeklyOffPolicies().catch(() => ({ success: false, data: {} })),
          attendanceStructureService.getHolidays().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getHolidayPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeHolidayPolicies().catch(() => ({ success: false, data: {} })),
        ]);
        await syncAttendancePoliciesCache();
        const normalizedWo = normalizeWeeklyOffPolicies(woResp?.data || []);
        const normalizedMappings = normalizeEmployeeWeeklyOffMapping(
          woMapResp?.data || {}
        );
        setWeeklyOffPolicies(normalizedWo);
        setEmployeeWeeklyOffMappings(normalizedMappings);
        cacheWeeklyOffStructureData(normalizedWo, normalizedMappings);
        setHolidaysData(holidaysResp?.data || []);
        setHolidayPolicies(hpResp?.data || []);
        setEmployeeHolidayMappings(hpMapResp?.data || {});
      } catch (e) {
        console.warn('Failed to load attendance policies:', e);
      }
    };
    loadPolicies();
  }, []);

  // Fetch attendance data for selected month (always fetch so employee sees data like admin view)
  useEffect(() => {
    let cancelled = false;
    const fetchAttendance = async ({ showLoading = true } = {}) => {
      if (!employee) {
        setAttendanceData({});
        return;
      }

      try {
        if (showLoading) setLoadingAttendance(true);
        const response = await attendanceService.getMyAttendanceHistory(
          selectedMonth + 1, // API expects 1-12
          selectedYear
        );
        if (cancelled) return;

        if (response.success && response.data && Array.isArray(response.data)) {
          const attendanceMap = {};
          const detailMap = {};
          const formatClock = (v, dateIso) =>
            formatPunchDateTimeDisplay(v, dateIso) || (v && v !== '--' ? String(v) : '--');
          response.data.forEach((record) => {
            const raw = record.date;
            if (!raw || typeof raw !== 'string') return;
            const dateIso = raw.trim().slice(0, 10);
            const parts = raw.trim().split(/[-/]/);
            if (parts.length < 3) return;
            const day = parseInt(parts[2], 10);
            if (Number.isNaN(day) || day < 1 || day > 31) return;
            const dayKey = String(day);
            const inRaw =
              record.checkIn ||
              record.checkInTime ||
              record.inTime ||
              record.firstIn ||
              record.firstPunchIn;
            const outRaw =
              record.checkOut ||
              record.checkOutTime ||
              record.outTime ||
              record.lastOut ||
              record.lastPunchOut;
            const inMs = parsePunchInstantMs(inRaw);
            const outMs = parsePunchInstantMs(outRaw);
            const outForDisplay =
              inMs != null && outMs != null && outMs <= inMs ? null : outRaw;
            attendanceMap[day] = record.status || '-';
            detailMap[dayKey] = {
              status: record.status || '-',
              sandwichApplied: !!(
                record.sandwichApplied === true ||
                record.sandwich_applied === true ||
                record.sandwichApplied === 1 ||
                record.sandwich_applied === 1
              ),
              checkIn: formatClock(inRaw, dateIso),
              checkOut: formatClock(outForDisplay, dateIso),
              totalHours: resolveDayDetailHours(
                inRaw,
                outForDisplay,
                record.totalHours ||
                  record.workedHours ||
                  record.workingHours ||
                  record.totalWorkedHours
              ),
              punches: Array.isArray(record.punches) ? record.punches : [],
              shiftAssigned:
                record.shiftAssigned ||
                record.shiftName ||
                record.shift ||
                record.assignedShift ||
                '--',
            };
          });
          setAttendanceData(attendanceMap);
          setAttendanceDetailData(detailMap);
          const todayKey = getLocalDayKey();
          const todayRec = response.data.find(
            (r) => r?.date && String(r.date).slice(0, 10) === todayKey
          );
          const empId = employee?.id || employee?.employeeId;
          if (todayRec && empId) {
            hydrateEmployeeWorklogFromAttendance(empId, todayRec);
          }
        } else {
          setAttendanceData({});
          setAttendanceDetailData({});
        }
        try {
          const { hydrateCompOffOtDeskFromServer } = await import('../lib/compOffOtNotify');
          await hydrateCompOffOtDeskFromServer({ employeeId: employee?.id || employee?.employeeId });
          if (!cancelled) setWoDeskTick((n) => n + 1);
        } catch {
          /* Change WO calendar paint still uses localStorage if hydrate fails */
        }
      } catch (error) {
        console.error('Failed to fetch attendance:', error);
        if (!cancelled) setAttendanceData({});
      } finally {
        if (!cancelled && showLoading) setLoadingAttendance(false);
      }
    };

    fetchAttendance({ showLoading: true });

    const onPunch = (e) => {
      const punchEmp = String(e?.detail?.employeeId ?? '');
      const selfId = String(employee?.id ?? employee?.employeeId ?? '');
      if (selfId && punchEmp && punchEmp !== selfId) return;
      window.setTimeout(() => fetchAttendance({ showLoading: false }), 400);
    };
    const onDeskOrLeaveChanged = () => {
      window.setTimeout(() => fetchAttendance({ showLoading: false }), 500);
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.addEventListener('admin:leave-request-changed', onDeskOrLeaveChanged);
    window.addEventListener('admin:quick-action-status', onDeskOrLeaveChanged);
    window.addEventListener('admin:quick-action-submitted', onDeskOrLeaveChanged);
    const onSocketAttendance = (payload) => {
      const punchEmp = String(payload?.employeeId ?? payload?.employee_id ?? '');
      const selfId = String(employee?.id ?? employee?.employeeId ?? '');
      if (selfId && punchEmp && punchEmp !== selfId) return;
      window.setTimeout(() => fetchAttendance({ showLoading: false }), 200);
    };
    if (socket?.on) {
      socket.on('attendance-updated', onSocketAttendance);
      socket.on('attendance-fast-updated', onSocketAttendance);
    }
    return () => {
      cancelled = true;
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
      window.removeEventListener('admin:leave-request-changed', onDeskOrLeaveChanged);
      window.removeEventListener('admin:quick-action-status', onDeskOrLeaveChanged);
      window.removeEventListener('admin:quick-action-submitted', onDeskOrLeaveChanged);
      if (socket?.off) {
        socket.off('attendance-updated', onSocketAttendance);
        socket.off('attendance-fast-updated', onSocketAttendance);
      }
    };
  }, [employee, selectedMonth, selectedYear]);

  const today = new Date();
  const todayNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

  // Check if a date is in the future (date-only comparison, no time)
  const isFutureDate = useCallback((day) => {
    const cellNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
    return cellNum > todayNum;
  }, [selectedYear, selectedMonth, todayNum]);

  // Summary counts: fill empty structural WO/PH via same resolver as Admin calendar
  const effectiveAttendance = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const result = { ...attendanceData };
    if (!employee) return result;

    for (let day = 1; day <= daysInMonth; day++) {
      if (result[day] && result[day] !== '-') continue;
      if (result[String(day)] && result[String(day)] !== '-') continue;
      const date = new Date(selectedYear, selectedMonth, day);
      const def = getDefaultStatus(
        date,
        employee,
        weeklyOffPolicies,
        employeeWeeklyOffMappings,
        holidaysData,
        holidayPolicies,
        employeeHolidayMappings
      );
      const head = String(def || '').toUpperCase().split('/')[0];
      if (head === 'WO' || head === 'PH' || head === 'HO') {
        result[day] = def;
        result[String(day)] = def;
      }
    }
    return result;
  }, [
    employee,
    attendanceData,
    selectedMonth,
    selectedYear,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
    woDeskTick,
  ]);

  const popupAttendanceData = useMemo(() => {
    const empId = String(employee?.id || employee?.employeeId || '');
    if (!empId) return {};
    // Raw stored statuses — calendar buildMonthPolicyStatusMap + getDayDefault paint WO/PH (C5)
    return {
      [empId]: Object.fromEntries(
        Object.entries(attendanceData || {}).map(([day, status]) => [
          String(day),
          status || '-',
        ])
      ),
    };
  }, [employee?.id, employee?.employeeId, attendanceData]);

  const popupAttendanceDetailData = useMemo(() => {
    const empId = String(employee?.id || employee?.employeeId || '');
    if (!empId) return {};
    return { [empId]: { ...(attendanceDetailData || {}) } };
  }, [employee?.id, employee?.employeeId, attendanceDetailData]);

  const popupEmployee = useMemo(() => {
    if (!employee) return null;
    return {
      ...employee,
      id: employee.id || employee.employeeId,
      code: employee.code || employee.empCode || employee.employeeId,
      name: employee.name || employee.employeeName,
    };
  }, [employee]);

  const handlePopupStatusEdit = useCallback((emp, day, combinedStatus) => {
    if (!day) return;
    setAttendanceData((prev) => ({ ...prev, [day]: combinedStatus }));
  }, []);

  const [deskFormOpen, setDeskFormOpen] = useState(false);
  const [deskFormContext, setDeskFormContext] = useState(null);
  const [deskFormDraft, setDeskFormDraft] = useState(emptyAttendanceDeskDraft);
  const [deskFormSubmitting, setDeskFormSubmitting] = useState(false);

  const openDeskForm = useCallback((actionLabel, draftOverrides = {}) => {
    if (!employee?.id && !employee?.employeeId) {
      toast.error('Your employee profile is required to raise this request.');
      return;
    }
    const empId = String(employee.id || employee.employeeId);
    const currentUser = getAuthJson('currentUser') || {};
    setDeskFormContext({
      actionLabel,
      employeeId: empId,
      employeeCode:
        employee?.empCode || employee?.employeeCode || employee?.code || null,
      userId: employee?.user?.id || currentUser?.id || null,
      userName:
        employee?.name ||
        employee?.fullName ||
        employee?.employeeName ||
        currentUser?.name ||
        'Employee',
      userEmail: employee?.email || currentUser?.email || '',
      gender: employee?.gender || '',
      requestedAt: Date.now(),
      source: 'ess_attendance_logs',
    });
    setDeskFormDraft({
      ...emptyAttendanceDeskDraft(),
      ...draftOverrides,
    });
    setDeskFormOpen(true);
  }, [employee]);

  const closeDeskForm = useCallback(() => {
    setDeskFormOpen(false);
    setDeskFormContext(null);
    setDeskFormSubmitting(false);
  }, []);

  const openApplyLeave = useCallback(
    (_emp, overrides = {}) => {
      const dateIso = overrides?.fromDate || overrides?.date || '';
      openDeskForm('Leave', {
        fromDate: dateIso,
        toDate: overrides?.toDate || dateIso,
        date: dateIso,
      });
    },
    [openDeskForm]
  );

  const openChangeShift = useCallback(
    (_emp, overrides = {}) => openDeskForm('Change Shift', overrides),
    [openDeskForm]
  );
  const openChangeWo = useCallback(
    (_emp, overrides = {}) => openDeskForm(CHANGE_WO_ACTION_LABEL, overrides),
    [openDeskForm]
  );
  const openShortLeave = useCallback(
    (_emp, overrides = {}) => openDeskForm('Short Leave', overrides),
    [openDeskForm]
  );
  const openOdRequest = useCallback(
    (_emp, overrides = {}) => openDeskForm('On Duty', overrides),
    [openDeskForm]
  );
  const openCompOffRequest = useCallback(
    (_emp, overrides = {}) => openDeskForm('Comp Off', overrides),
    [openDeskForm]
  );
  const openOtRequest = useCallback(
    (_emp, overrides = {}) => openDeskForm('Overtime', overrides),
    [openDeskForm]
  );
  const openRhRequest = useCallback(
    (_emp, overrides = {}) => openDeskForm('Restricted Holiday', overrides),
    [openDeskForm]
  );

  useEffect(() => {
    if (!employee?.id) return;
    if (!consumeOpenRestrictedHolidayForm()) return;
    openRhRequest(employee);
  }, [employee, openRhRequest]);

  const showRhToolbar = useMemo(
    () =>
      employeeHasRestrictedHolidayOption({
        employeeId: employee?.id,
        holidayPolicies,
        employeeHolidayMappings,
      }),
    [employee?.id, holidayPolicies, employeeHolidayMappings]
  );

  const openLeaveBalance = useCallback(() => {
    if (!employee) {
      toast.error('Employee profile not loaded');
      return;
    }
    setShowLeaveBalancePopup(true);
  }, [employee]);

  const openCompOffLedger = useCallback(() => {
    if (!employee) {
      toast.error('Employee profile not loaded');
      return;
    }
    setShowCompOffLedgerPopup(true);
  }, [employee]);

  const openViewChecks = useCallback((_emp, dateIso) => {
    if (!employee) {
      toast.error('Employee profile not loaded');
      return;
    }
    setViewChecksDate(dateIso || null);
    setShowViewChecksPopup(true);
  }, [employee]);

  const submitDeskForm = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (deskFormSubmitting || !deskFormContext?.employeeId) return;

      const actionLabel = deskFormContext.actionLabel;
      const formData = { ...deskFormDraft };

      if (actionLabel === 'Change Shift') {
        if (!String(formData?.shift || '').trim()) {
          toast.error('Select a new shift.');
          return;
        }
        if (!String(formData?.date || '').trim()) {
          toast.error('Select an effective date.');
          return;
        }
      }
      if (actionLabel === CHANGE_WO_ACTION_LABEL) {
        if (!String(formData?.date || '').trim()) {
          toast.error('Select a Weekly Off date.');
          return;
        }
        if (!String(formData?.reason || '').trim()) {
          toast.error('Enter a reason.');
          return;
        }
      }
      if (actionLabel === 'Short Leave' || actionLabel === 'Overtime') {
        if (!String(formData?.date || '').trim()) {
          toast.error('Select a date.');
          return;
        }
        if (
          !String(formData?.startTime || '').trim() ||
          !String(formData?.endTime || '').trim()
        ) {
          toast.error('Enter start and end time.');
          return;
        }
      }
      if (
        (actionLabel === 'On Duty' || actionLabel === 'Comp Off') &&
        !String(formData?.date || '').trim()
      ) {
        toast.error('Select a date.');
        return;
      }
      if (actionLabel === 'Restricted Holiday') {
        if (!String(formData?.holidayName || '').trim() && formData?.holidayId == null) {
          toast.error('Select a Restricted Holiday from the list.');
          return;
        }
        if (!String(formData?.date || '').trim()) {
          toast.error('Select a Restricted Holiday from the list.');
          return;
        }
      }
      if (actionLabel === 'Leave') {
        if (!String(formData?.leaveType || '').trim()) {
          toast.error('Select a leave type.');
          return;
        }
        if (!String(formData?.fromDate || '').trim()) {
          toast.error('Select from date.');
          return;
        }
      }

      setDeskFormSubmitting(true);
      try {
        const common = {
          employeeId: deskFormContext.employeeId,
          employeeCode: deskFormContext.employeeCode || null,
          userName: deskFormContext.userName || 'Employee',
          userId: deskFormContext.userId || null,
          userEmail: deskFormContext.userEmail || '',
          formData,
          mode: 'ess',
        };

        if (actionLabel === 'Leave') {
          const apiRes = await attendanceStructureService.createLeaveRequest({
            employeeId: Number(deskFormContext.employeeId),
            leaveStatusCode: formData.leaveType,
            leaveDuration: formData.leaveDuration || 'full_day',
            fromDate: formData.fromDate,
            toDate: formData.toDate || formData.fromDate,
            reason: formData.reason,
            notes: formData.notes,
          });
          if (apiRes?.autoApproved) {
            toast.success('Leave auto-approved and applied');
          } else {
            toast.success('Leave submitted for approval');
          }
          window.dispatchEvent(new CustomEvent('admin:leave-request-changed'));
        } else if (actionLabel === 'Change Shift') {
          await submitChangeShiftRequest(common);
          toast.success('Change Shift submitted for approval');
        } else if (actionLabel === CHANGE_WO_ACTION_LABEL) {
          const entry = await submitChangeWoRequest({
            ...common,
            weeklyOffPolicies,
            employeeWeeklyOffMappings,
          });
          toast.success(
            entry?.status === 'Approved'
              ? 'Change WO auto-approved — Current WO is working day; Proposed WO is Weekly Off'
              : 'Change WO submitted for approval'
          );
        } else if (actionLabel === 'Short Leave') {
          await submitShortLeaveRequest(common);
          toast.success('Short Leave submitted for approval');
        } else if (actionLabel === 'On Duty') {
          await submitAttendanceActionRequest({ ...common, actionLabel: 'On Duty' });
          toast.success('On Duty request submitted for approval');
        } else if (actionLabel === 'Comp Off') {
          await submitCompOffRequest(common);
          toast.success('Comp Off work request submitted for approval');
        } else if (actionLabel === 'Overtime') {
          await submitOvertimeRequest(common);
          toast.success('Overtime request submitted for approval');
        } else if (actionLabel === 'Restricted Holiday') {
          await submitRestrictedHolidayRequest(common);
          toast.success('Restricted Holiday submitted for approval');
        } else {
          toast.error('Unsupported action.');
          return;
        }
        closeDeskForm();
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err?.message || 'Failed to submit request'
        );
      } finally {
        setDeskFormSubmitting(false);
      }
    },
    [deskFormSubmitting, deskFormContext, deskFormDraft, closeDeskForm, weeklyOffPolicies, employeeWeeklyOffMappings]
  );

  // Paid/unpaid leave codes for pay-days calculation (same logic as admin)
  const { unpaidLeaveCodes, paidLeaveCodes } = useMemo(() => {
    const unpaid = new Set(['LOP']);
    const paid = new Set(['PR', 'PH', 'WO']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (!code) return;
      if (lt.isPaid === false || lt.is_paid === false) unpaid.add(code);
      else paid.add(code);
    });
    return { unpaidLeaveCodes: unpaid, paidLeaveCodes: paid };
  }, [legendLeaveTypes]);

  // Known status codes for splitting combined statuses (e.g. PRCL -> PR + CL)
  const knownStatusCodes = useMemo(() => {
    const set = new Set(['PR', 'PH', 'WO', 'CL', 'EL', 'CO', 'CW', 'LOP']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (code) set.add(code);
    });
    return set;
  }, [legendLeaveTypes]);

  // Status counts for the selected month (uses effectiveAttendance so when locked PR/WO/PH are counted)
  const statusCounts = useMemo(() => {
    const counts = { PR: 0, PH: 0, WO: 0, CL: 0, EL: 0, CO: 0, CW: 0, LOP: 0 };
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    let joinDateNum = 0;
    let relievedDateNum = 99999999;
    if (employee?.joiningDate) {
      const d = new Date(employee.joiningDate);
      if (!isNaN(d.getTime())) joinDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const relieved = employee?.relievedDate || employee?.exitDate || employee?.relievingDate;
    if (relieved) {
      const d = new Date(relieved);
      if (!isNaN(d.getTime())) relievedDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const normalize = (s) => String(s || '').trim().toUpperCase();
    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
      if (currentNum < joinDateNum || currentNum > relievedDateNum) continue;
      let status = effectiveAttendance[day];
      if (!status || status === '-') continue;
      status = normalize(status);
      // Combined status (e.g. PRCL, LWPPR)
      const trySplit = (str) => {
        if (str.length <= 3 && knownStatusCodes.has(str)) return [str];
        for (let len = 3; len >= 2; len--) {
          const first = str.substring(0, len);
          const rest = str.substring(len);
          if (knownStatusCodes.has(first) && rest && knownStatusCodes.has(rest)) return [first, rest];
        }
        return [str];
      };
      const parts = trySplit(status);
      if (parts.length >= 2) {
        parts.forEach((p) => {
          const key = normalize(p);
          if (counts[key] === undefined) counts[key] = 0;
          counts[key] += 0.5;
        });
      } else {
        const key = parts[0] ? normalize(parts[0]) : status;
        if (counts[key] === undefined) counts[key] = 0;
        counts[key] += 1;
      }
    }
    return counts;
  }, [effectiveAttendance, selectedMonth, selectedYear, employee, knownStatusCodes]);

  // Pay days = sum of paid status counts (same logic as admin)
  const payDays = useMemo(() => {
    let total = 0;
    Object.entries(statusCounts || {}).forEach(([code, val]) => {
      const c = Number(val) || 0;
      if (c === 0) return;
      const upper = String(code).toUpperCase();
      if (unpaidLeaveCodes && unpaidLeaveCodes.has(upper)) return;
      if (upper === 'LOP') return;
      if (paidLeaveCodes && paidLeaveCodes.has(upper)) total += c;
      else total += c; // unknown code treat as paid
    });
    return total;
  }, [statusCounts, paidLeaveCodes, unpaidLeaveCodes]);

  // Totals object for the Totals bar (like admin "Calculate and Show Summary")
  const totals = useMemo(() => ({
    ...statusCounts,
    payDays,
  }), [statusCounts, payDays]);

  // Selectable codes only: from attendance config + leave types (same as admin dropdown). No hardcoding.
  const selectableCodesOrdered = useMemo(() => {
    const fromConfig = (legendConfigs || [])
      .filter((c) => c.customCode || c.originalCode || c.original_code)
      .map((c) => String(c.customCode || c.originalCode || c.original_code || '').toUpperCase())
      .filter(Boolean);
    const configSet = new Set(fromConfig);
    const fromLeave = (legendLeaveTypes || [])
      .filter((lt) => lt.code && (lt.isActive !== false && lt.is_active !== false))
      .map((lt) => String(lt.code || '').toUpperCase())
      .filter((code) => code && !configSet.has(code));
    return [...fromConfig, ...fromLeave];
  }, [legendConfigs, legendLeaveTypes]);

  // Full form per code: from config.meaning or leaveType.name only (no hardcoded map)
  const fullFormByCode = useMemo(() => {
    const map = { payDays: 'Pay Days' };
    (legendConfigs || []).forEach((c) => {
      const code = String(c.customCode || c.originalCode || c.original_code || '').toUpperCase();
      if (code && (c.meaning || c.meaning === 0)) map[code] = c.meaning;
    });
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || '').toUpperCase();
      if (code && (lt.name || lt.name === 0)) map[code] = lt.name;
    });
    return map;
  }, [legendConfigs, legendLeaveTypes]);

  const getFullForm = useCallback((code) => {
    if (code === 'payDays') return fullFormByCode.payDays || 'Pay Days';
    const upper = String(code || '').toUpperCase();
    return fullFormByCode[upper] ?? (upper || code);
  }, [fullFormByCode]);

  // Legend entries: only selectable codes + payDays, each with full form from API (config/leave types)
  const legendEntries = useMemo(() => {
    const keys = [...selectableCodesOrdered, 'payDays'];
    return keys.map((code) => {
      const label = getFullForm(code);
      const upper = code === 'payDays' ? code : String(code).toUpperCase();
      const style = code === 'payDays'
        ? 'rh-accent-soft border-redhalo-teal/60'
        : (statusStyles[upper] || 'bg-gray-100 text-gray-800 border-gray-200');
      const leaveType = legendLeaveTypes?.find((lt) => String(lt.code || '').toUpperCase() === upper);
      const color = leaveType?.color ? (leaveType.color.startsWith('#') ? leaveType.color : `#${leaveType.color}`) : null;
      return { code: upper === code ? code : upper, label, style, color, isPayDays: code === 'payDays' };
    });
  }, [selectableCodesOrdered, getFullForm, legendLeaveTypes]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-md:p-4">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden max-md:overflow-visible max-md:rounded-none max-md:bg-transparent max-md:shadow-none">
        {showRhToolbar && !isLocked ? (
          <div className="flex items-center justify-end gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 max-md:mb-3 max-md:rounded-xl max-md:border max-md:px-3">
            <button
              type="button"
              onClick={() => openRhRequest(employee)}
              className="rounded-xl bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] max-md:min-h-[44px] max-md:flex-1 max-md:text-sm"
            >
              Apply Restricted Holiday
            </button>
            <button
              type="button"
              title="Restricted Holiday dates are on your holiday calendar. Apply RH for approval by your Reporting Manager."
              aria-label="Restricted Holiday information"
              className="inline-flex shrink-0 items-center justify-center rounded-lg p-1 text-slate-500 hover:bg-slate-100 max-md:h-11 max-md:w-11"
            >
              <Info className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ) : null}
        <div className="h-[74.1vh] min-h-[589px] p-[1.425rem] max-md:h-auto max-md:min-h-0 max-md:p-0">
          {popupEmployee && (
            <EmployeeAttendanceCalendarPopup
              embedded
              darkMode={false}
              isOpen
              onClose={() => {}}
              employee={popupEmployee}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              attendanceData={popupAttendanceData}
              attendanceDetailData={popupAttendanceDetailData}
              leaveTypes={legendLeaveTypes}
              isLocked={isLocked}
              attendanceLocks={attendanceLocks}
              showProcessStatusSummary={isLocked}
              showFinalisedBadge={isLocked}
              onEditAttendanceStatus={handlePopupStatusEdit}
              onSaveRegularizationTimes={() => {}}
              onApplyLeave={openApplyLeave}
              onLeaveBalance={canViewLeaveBalance ? openLeaveBalance : null}
              onCompOffLedger={canViewCompOffLedger ? openCompOffLedger : null}
              onViewEditChecks={canViewChecks ? openViewChecks : null}
              canViewEditChecks={false}
              viewChecksReadOnly
              onChangeShift={openChangeShift}
              onChangeWo={openChangeWo}
              onShortLeave={openShortLeave}
              onOdRequest={openOdRequest}
              onCompOffRequest={openCompOffRequest}
              onOtRequest={openOtRequest}
              onRhRequest={openRhRequest}
              weeklyOffPolicies={weeklyOffPolicies}
              employeeWeeklyOffMappings={employeeWeeklyOffMappings}
              holidaysData={holidaysData}
              holidayPolicies={holidayPolicies}
              employeeHolidayMappings={employeeHolidayMappings}
              getDayDefault={(date, emp) =>
                getDefaultStatus(
                  date,
                  emp || popupEmployee,
                  weeklyOffPolicies,
                  employeeWeeklyOffMappings,
                  holidaysData,
                  holidayPolicies,
                  employeeHolidayMappings
                )
              }
              requireQuickPanelForDayActions
            />
          )}
        </div>
      </div>

      {showLeaveBalancePopup && employee && (
        <LeaveBalancePopup
          darkMode={false}
          isOpen={showLeaveBalancePopup}
          onClose={() => setShowLeaveBalancePopup(false)}
          employee={employee}
          leaveTypes={legendLeaveTypes}
          attendanceStructureService={attendanceStructureService}
        />
      )}

      {showCompOffLedgerPopup && employee && (
        <CompOffLedgerPopup
          darkMode={false}
          isOpen={showCompOffLedgerPopup}
          onClose={() => setShowCompOffLedgerPopup(false)}
          employee={employee}
        />
      )}

      {showViewChecksPopup && employee && (
        <ViewEditChecksPopup
          darkMode={false}
          isOpen={showViewChecksPopup}
          onClose={() => {
            setShowViewChecksPopup(false);
            setViewChecksDate(null);
          }}
          employee={employee}
          readOnly
          initialDate={viewChecksDate}
          lockDate={!!viewChecksDate}
          canCreate={false}
          canEdit={false}
          canDelete={false}
        />
      )}

      <QuickRequestFormModal
        darkMode={false}
        isOpen={deskFormOpen}
        actionLabel={deskFormContext?.actionLabel || 'Leave'}
        context={deskFormContext}
        quickActionDraft={deskFormDraft}
        setQuickActionDraft={setDeskFormDraft}
        maxDateToday={getLocalDayKey()}
        onClose={closeDeskForm}
        onSubmitQuickAction={submitDeskForm}
        isSubmitting={deskFormSubmitting}
      />
    </div>
  );
}
